import { ActionMap, StoreActions } from '../reducer.types';
import {
  UserStoreActionTypes,
  UserStoryState,
  UserStoryStorePayload,
  SectionType
} from './UserStoryStore.types';
import { NO_EPIC_KEY, JIRA_EPIC_KEY, TASK_ID_PREFIX } from '../../common/Constants';
import { EpicToStoryMap, EpicsMap, Note, StoriesMap } from '../../common/Types';

export type UserStoryStoreActions =
  ActionMap<UserStoryStorePayload>[keyof ActionMap<UserStoryStorePayload>];

const getKey = (epicToStoryMap: EpicToStoryMap, epicId?: string) => {
  let key = NO_EPIC_KEY;
  if (epicId) {
    key = epicToStoryMap[epicId] ? epicId : JIRA_EPIC_KEY;
  }
  return key;
};

// Helper to add section types to the creation order if not already present
const addSectionToOrder = (currentOrder: SectionType[], ...sectionsToAdd: SectionType[]): SectionType[] => {
  const newOrder = [...currentOrder];
  for (const section of sectionsToAdd) {
    if (!newOrder.includes(section)) {
      newOrder.push(section);
    }
  }
  return newOrder;
};

// Determine section types from story IDs
const getSectionTypesFromStoryIds = (storyIds: string[]): SectionType[] => {
  const types: SectionType[] = [];
  const hasStories = storyIds.some(id => !id.startsWith(`${TASK_ID_PREFIX}_`));
  const hasTasks = storyIds.some(id => id.startsWith(`${TASK_ID_PREFIX}_`));
  if (hasStories) types.push('stories');
  if (hasTasks) types.push('tasks');
  return types;
};

export const UserStoryStoreReducer = (state: UserStoryState, action: StoreActions) => {
  switch (action.type) {
    case UserStoreActionTypes.BulkCreateEpic: {
      const epics = Object.keys(action.payload.epicCollection.epics);
      const epicMap: { [key: string]: string[] } = {};
      epics.map(function (item) {
        action.payload.epicCollection.epics[item] = {
          ...action.payload.epicCollection.epics[item],
          isNew: true
        };
        epicMap[item] = [];
      });
      return {
        ...state,
        notes: [...state.notes, action.payload.userNotes],
        epics: { ...state.epics, ...action.payload.epicCollection.epics },
        nextEpicIncrementKey: state.nextEpicIncrementKey + epics.length,
        epicToStoryMap: { ...state.epicToStoryMap, ...epicMap },
        sectionCreationOrder: addSectionToOrder(state.sectionCreationOrder, 'epics')
      };
    }
    case UserStoreActionTypes.BulkCreateStory: {
      const newStories = Object.keys(action.payload.storyCollection.stories);
      const selectedEpic = state.selectedEpic ?? NO_EPIC_KEY;
      const existingStories: string[] = state.epicToStoryMap[selectedEpic] || [];
      const notes = action.payload.skipHistoryPrompt
        ? [...state.notes]
        : [...state.notes, action.payload.userNotes];

      const mappedStories = { ...action.payload.storyCollection.stories };
      newStories.forEach((id) => {
        mappedStories[id] = {
          ...mappedStories[id],
          isNew: true
        };
      });
      
      // Determine if creating stories or tasks (or both) and track section order
      const newSectionTypes = getSectionTypesFromStoryIds(newStories);

      return {
        ...state,
        stories: { ...state.stories, ...mappedStories },
        notes: notes,
        epicToStoryMap: {
          ...state.epicToStoryMap,
          [selectedEpic]: [...existingStories, ...newStories]
        },
        nextStoryIncrementKey: state.nextStoryIncrementKey + newStories.length,
        sectionCreationOrder: addSectionToOrder(state.sectionCreationOrder, ...newSectionTypes)
      };
    }
    case UserStoreActionTypes.BulkCreateEpicWithStory: {
      const { epics, stories } = action.payload.epicWithStoryCollection;
      const epicKeys = Object.keys(epics);
      const storyKeys = Object.keys(stories);

      const mappedEpics = { ...epics };
      epicKeys.map(function (item) {
        mappedEpics[item] = {
          ...mappedEpics[item],
          isNew: true
        };
      });

      const epicMap: { [key: string]: string[] } = Object.entries(stories).reduce(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (epicMap: { [key: string]: string[] }, [key, val]) => {
          const epicKey = val.epic_id ?? NO_EPIC_KEY;
          epicMap[epicKey] = epicMap[epicKey] || [];
          epicMap[epicKey].push(val.id);
          return epicMap;
        },
        {}
      );
      const mappedStories = { ...stories };
      storyKeys.forEach((id) => {
        mappedStories[id] = {
          ...mappedStories[id],
          isNew: true
        };
      });
      
      // Track section creation order - epics first, then stories/tasks
      const newSectionTypes = getSectionTypesFromStoryIds(storyKeys);
      const sectionsToAdd: SectionType[] = epicKeys.length > 0 ? ['epics', ...newSectionTypes] : newSectionTypes;

      return {
        ...state,
        epics: { ...state.epics, ...mappedEpics },
        stories: { ...state.stories, ...mappedStories },
        notes: [...state.notes, action.payload.userNotes],
        epicToStoryMap: { ...state.epicToStoryMap, ...epicMap },
        nextStoryIncrementKey: state.nextStoryIncrementKey + storyKeys.length,
        nextEpicIncrementKey: state.nextEpicIncrementKey + epicKeys.length,
        sectionCreationOrder: addSectionToOrder(state.sectionCreationOrder, ...sectionsToAdd)
      };
    }
    case UserStoreActionTypes.SaveUserNotesHistory: {
      const newNotes: Note = action.payload;
      return {
        ...state,
        notes: [...state.notes, newNotes]
      };
    }
    case UserStoreActionTypes.DeleteEpic: {
      const epicId = action.payload;
      const updatedEpics = { ...state.epics };
      delete updatedEpics[epicId];

      const updatedStories = { ...state.stories };
      const storiesToDelete = state.epicToStoryMap[epicId] ?? [];
      storiesToDelete.forEach((storyId) => {
        delete updatedStories[storyId];
      });

      const updatedEpicMap = { ...state.epicToStoryMap };
      delete updatedEpicMap[epicId];

      return {
        ...state,
        epics: updatedEpics,
        stories: updatedStories,
        epicToStoryMap: updatedEpicMap
      };
    }
    case UserStoreActionTypes.RemoveEpic: {
      const epicId = action.payload;
      const updatedEpics = { ...state.epics };
      delete updatedEpics[epicId];

      const updatedEpicMap: EpicToStoryMap = { ...state.epicToStoryMap };
      const epicStoryIds = updatedEpicMap[epicId] ?? [];
      delete updatedEpicMap[epicId];

      const existingUnassigned = updatedEpicMap[NO_EPIC_KEY] ?? [];
      updatedEpicMap[NO_EPIC_KEY] = Array.from(
        new Set([...existingUnassigned, ...epicStoryIds])
      );

      const updatedStories: StoriesMap = { ...state.stories };
      epicStoryIds.forEach((storyId) => {
        const story = updatedStories[storyId];
        if (!story) return;
        updatedStories[storyId] = { ...story, epic_id: undefined };
      });

      return {
        ...state,
        epics: updatedEpics,
        stories: updatedStories,
        epicToStoryMap: updatedEpicMap
      };
    }
    case UserStoreActionTypes.BulkDeleteEpic: {
      const epicsToDelete = action.payload;
      const updatedEpics = { ...state.epics };
      const updatedStories = { ...state.stories };
      const updatedEpicMap = { ...state.epicToStoryMap };

      epicsToDelete.forEach((epicId) => {
        const storiesToDelete = state.epicToStoryMap[epicId] ?? [];
        storiesToDelete.forEach((storyId) => {
          delete updatedStories[storyId];
        });
        delete updatedEpicMap[epicId];
        delete updatedEpics[epicId];
      });

      return {
        ...state,
        epics: updatedEpics,
        stories: updatedStories,
        epicToStoryMap: updatedEpicMap
      };
    }
    case UserStoreActionTypes.DeleteStory: {
      const updatedStories = { ...state.stories };
      const updatedStoryMap = { ...state.epicToStoryMap };

      action.payload.map((storyId) => {
        const epicId = state.stories[storyId]?.epic_id;
        const epicKey = getKey(state.epicToStoryMap, epicId);
        const index = updatedStoryMap[epicKey].indexOf(storyId, 0);
        if (index > -1) {
          updatedStoryMap[epicKey].splice(index, 1);
        }
        delete updatedStories[storyId];
      });

      return {
        ...state,
        stories: updatedStories,
        epicToStoryMap: updatedStoryMap
      };
    }
    case UserStoreActionTypes.UpdateEpic: {
      const { id, value } = action.payload;
      return {
        ...state,
        epics: { ...state.epics, [id]: value }
      };
    }
    case UserStoreActionTypes.UpdateStory: {
      const { id, value } = action.payload;
      const oldEpicId = state.stories[id].epic_id || '';
      const newEpicId = value.epic_id || '';
      // Get the old epic id of the story where it was
      const oldStoryEpicKey = getKey(state.epicToStoryMap, oldEpicId);
      const currentEpicKey = getKey(state.epicToStoryMap, value.epic_id);
      const updatedEpicMap = { ...state.epicToStoryMap };
      if (newEpicId !== oldEpicId) {
        updatedEpicMap[oldStoryEpicKey] = updatedEpicMap[oldStoryEpicKey].filter(
          (storyId) => storyId !== id
        );
        updatedEpicMap[currentEpicKey] = [...updatedEpicMap[currentEpicKey], id];
      }

      return {
        ...state,
        stories: { ...state.stories, [id]: value },
        epicToStoryMap: updatedEpicMap
      };
    }
    case UserStoreActionTypes.AssignEpicToStory: {
      const { epicId, storyIds, isBulkAssign } = action.payload;
      const updatedStories = { ...state.stories };
      const updatedEpicToStoryMap = { ...state.epicToStoryMap };
      storyIds.map((storyId) => {
        const newEpickKey = getKey(state.epicToStoryMap, epicId);
        const oldEpicId = state.stories[storyId].epic_id;
        const oldEpicKey = getKey(state.epicToStoryMap, oldEpicId);

        if (oldEpicId !== epicId) {
          updatedStories[storyId] = {
            ...updatedStories[storyId],
            epic_id: epicId,
            isEpicChanged: isBulkAssign
          };
          if (oldEpicKey !== newEpickKey) {
            const index = updatedEpicToStoryMap[oldEpicKey].indexOf(storyId, 0);
            if (index > -1) {
              updatedEpicToStoryMap[oldEpicKey].splice(index, 1);
            }
            updatedEpicToStoryMap[newEpickKey] = [...updatedEpicToStoryMap[newEpickKey], storyId];
          }
        }
      });
      return {
        ...state,
        stories: updatedStories,
        epicToStoryMap: updatedEpicToStoryMap
      };
    }
    case UserStoreActionTypes.ResetStoryEpicChangeState: {
      const { storyIds, epicIds } = action.payload;
      const updatedStories = { ...state.stories };
      storyIds.forEach((id) => {
        updatedStories[id] = { ...updatedStories[id], isEpicChanged: undefined, isNew: undefined };
      });
      if (epicIds?.length) {
        const updatedEpics = { ...state.epics };
        epicIds.forEach((id) => {
          if (updatedEpics[id]) {
            updatedEpics[id] = { ...updatedEpics[id], isNew: undefined };
          }
        });
        return {
          ...state,
          stories: updatedStories,
          epics: updatedEpics
        };
      }
      return {
        ...state,
        stories: updatedStories
      };
    }
    case UserStoreActionTypes.SelectEpic: {
      return { ...state, selectedEpic: action.payload };
    }
    case UserStoreActionTypes.SelectEpicForBulk: {
      const updatedEpics = state.selectedEpics.filter((id) => !action.payload.includes(id));
      return { ...state, selectedEpics: [...updatedEpics, ...action.payload] };
    }
    case UserStoreActionTypes.UnSelectEpicForBulk: {
      const updatedSelectedEpics = state.selectedEpics.filter(
        (e) => !action.payload.includes(e)
      );
      return { ...state, selectedEpics: [...updatedSelectedEpics] };
    }
    case UserStoreActionTypes.SelectStory: {
      const updatedStories = state.selectedStories.filter((id) => !action.payload.includes(id));
      return { ...state, selectedStories: [...updatedStories, ...action.payload] };
    }
    case UserStoreActionTypes.UnSelectStory: {
      const updatedSelectedStories = state.selectedStories.filter(
        (s) => !action.payload.includes(s)
      );
      return { ...state, selectedStories: [...updatedSelectedStories] };
    }
    case UserStoreActionTypes.LoadJiraEpic: {
      return {
        ...state,
        jiraEpics: action.payload
      };
    }
    case UserStoreActionTypes.SetCurrentProject: {
      return { ...state, currentProject: action.payload };
    }
    case UserStoreActionTypes.ResetIssues: {
      return {
        ...state,
        epics: {},
        jiraEpics: {},
        stories: {},
        epicToStoryMap: {
          NO_EPIC: [],
          JIRA_EPIC: []
        },
        invalidJiraEpicStories: {},
        selectedEpic: null,
        selectedStories: [],
        selectedEpics: [],
        nextEpicIncrementKey: 1,
        nextStoryIncrementKey: 1,
        notes: [],
        sectionCreationOrder: []
      };
    }
    case UserStoreActionTypes.PublishToJira: {
      const { epics: epicResult, stories: storyResult } = action.payload;
      const { epics, stories, epicToStoryMap } = state;
      const unpublishedEpics: EpicsMap = {};
      const unpublishedEpicToStoryMap = {
        ...epicToStoryMap
      };
      // Keep only failed epics and stories
      const unpublishedStories: StoriesMap = {};
      Object.keys(stories).forEach((storyId: string) => {
        const index = storyResult.failed.indexOf(storyId);
        if (index > -1) {
          unpublishedStories[storyId] = { ...stories[storyId] };
        }
      });

      Object.keys(epics).forEach((epicId: string) => {
        const item = epicResult.success.find((item) => item.id === epicId);
        if (item) {
          // push all stories for the epic
          unpublishedEpicToStoryMap.JIRA_EPIC = [
            ...unpublishedEpicToStoryMap.JIRA_EPIC,
            ...epicToStoryMap[epicId].filter((id) => storyResult.failed.includes(id))
          ];
          delete unpublishedEpicToStoryMap[epicId];
          // update the story epic id
          epicToStoryMap[epicId].forEach(
            (storyId) =>
              (unpublishedStories[storyId] = {
                ...unpublishedStories[storyId],
                epic_id: item.jiraId
              })
          );
        } else {
          unpublishedEpics[epicId] = { ...epics[epicId] };
        }
      });

      // Remove published stories
      unpublishedEpicToStoryMap.JIRA_EPIC = unpublishedEpicToStoryMap.JIRA_EPIC.filter(
        (storyId) => !!unpublishedStories[storyId]
      );
      unpublishedEpicToStoryMap.NO_EPIC = unpublishedEpicToStoryMap.NO_EPIC.filter(
        (storyId) => !!unpublishedStories[storyId]
      );

      return {
        ...state,
        epics: { ...unpublishedEpics },
        stories: { ...unpublishedStories },
        epicToStoryMap: unpublishedEpicToStoryMap,
        selectedStories: [],
        selectedEpics: []
      };
    }
    // Add pending user message immediately (before API call)
    case UserStoreActionTypes.AddPendingUserMessage: {
      const pendingNote: Note = {
        ...action.payload,
        isPending: true,
        isNew: true
      };
      return {
        ...state,
        notes: [...state.notes, pendingNote]
      };
    }
    // Remove pending user message (on error)
    case UserStoreActionTypes.RemovePendingUserMessage: {
      const { timestamp } = action.payload;
      const filteredNotes = state.notes.filter(
        (note) => note.timestamp.getTime() !== timestamp.getTime() || !note.isPending
      );
      return {
        ...state,
        notes: filteredNotes
      };
    }
    // Mark pending message as failed (keep in history)
    case UserStoreActionTypes.MarkPendingUserMessageFailed: {
      const { timestamp } = action.payload;
      const updatedNotes = state.notes.map((note) => {
        if (note.timestamp.getTime() === timestamp.getTime() && note.isPending) {
          return { ...note, isPending: false, isFailed: true };
        }
        return note;
      });
      return {
        ...state,
        notes: updatedNotes
      };
    }
    default:
      return state;
  }
};
